<i18n src="./resources/locales.json"></i18n>

<template>
  <div class="agent-states-summary-bar">
    <div v-once class="agent-states-title">{{ $t('agentStates') }}</div>
    <div class="agent-states-bar">
      <div v-if="totalAgents === 0" class="state-bar-section no-agents">
        {{ $t('noAvailableAgents') }}
      </div>
      <div
        v-for="(state, index) in states"
        :key="index"
        class="state-bar-section"
        :style="{ width: `${state.value.percent}%`, backgroundColor: state.color }"
      >
        <div class="state-description">{{ state.value.count }} {{ state.label }}</div>
        <div class="agent-states-bar-detailed">
          <div
            v-for="(subState, subStateIndex) in state.subgroups"
            :key="subStateIndex"
            class="state-bar-sub-section"
            :style="{ width: `${subState.value.percent}%`, backgroundColor: subState.color }"
            @click="filterBpAgentsByStateId(subState.key)"
          >
            <div class="state-description">{{ subState.value.count }} {{ subState.label }}</div>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import { mapGetters } from 'vuex';
import { AGENT_STATE } from '@/constants/agent/constants';

export default {
  name: 'EcsAgentStatesSummaryBar',

  props: {
    businessProcessId: {
      type: String,
      required: true
    }
  },

  data() {
    return {};
  },

  computed: {
    ...mapGetters('STATS', ['getBusinessProcessStatsById']),

    businessProcessStats() {
      return this.getBusinessProcessStatsById(this.businessProcessId);
    },

    totalAgents() {
      const reducer = (sum, state) => sum + state.value.count;
      return this.states.reduce(reducer, 0);
    },

    states() {
      const total =
        this.businessProcessStats.totalReadyAgents +
          this.businessProcessStats.totalBusyAgents +
          this.businessProcessStats.totalBreakAgents || 1;

      return [
        {
          label: 'Ready',
          value: {
            percent: (this.businessProcessStats.totalReadyAgents * 100) / total,
            count: this.businessProcessStats.totalReadyAgents
          },
          color: 'var(--v-success-base)',
          subgroups: [
            {
              label: 'Ready',
              key: AGENT_STATE.ready,
              value: {
                percent: 100,
                count: this.businessProcessStats.totalReadyAgents
              },
              color: 'var(--v-success-darken1)'
            }
          ]
        },
        {
          label: 'Busy',
          value: {
            percent: (this.businessProcessStats.totalBusyAgents * 100) / total,
            count: this.businessProcessStats.totalBusyAgents
          },
          color: 'var(--v-error-base)',
          subgroups: [
            {
              label: 'Busy',
              key: AGENT_STATE.busy,
              value: {
                percent:
                  (this.businessProcessStats.getValue('businessProcessBusyAgents') * 100) /
                  (this.businessProcessStats.totalBusyAgents || 1),
                count: this.businessProcessStats.getValue('businessProcessBusyAgents')
              },
              color: 'var(--v-error-darken1)'
            },
            {
              label: 'Consult or conference',
              value: {
                percent:
                  (this.businessProcessStats.getValue('businessProcessConsultOrConferenceAgents') * 100) /
                  (this.businessProcessStats.totalBusyAgents || 1),
                count: this.businessProcessStats.getValue('businessProcessConsultOrConferenceAgents')
              },
              color: 'var(--v-error-darken2)'
            },
            {
              label: 'Backoffice',
              key: AGENT_STATE.backoffice,
              value: {
                percent:
                  (this.businessProcessStats.getValue('businessProcessBackofficeAgents') * 100) /
                  (this.businessProcessStats.totalBusyAgents || 1),
                count: this.businessProcessStats.getValue('businessProcessBackofficeAgents')
              },
              color: 'var(--v-error-darken3)'
            },
            {
              label: 'Outgoing',
              key: AGENT_STATE.outgoing,
              value: {
                percent:
                  (this.businessProcessStats.getValue('businessProcessForOutgoingAgents') * 100) /
                  (this.businessProcessStats.totalBusyAgents || 1),
                count: this.businessProcessStats.getValue('businessProcessForOutgoingAgents')
              },
              color: 'var(--v-error-darken3)'
            }
          ]
        },
        {
          label: 'Break',
          value: {
            percent: (this.businessProcessStats.totalBreakAgents * 100) / total,
            count: this.businessProcessStats.totalBreakAgents
          },
          color: 'var(--v-warning-darken1)',
          subgroups: [
            {
              label: 'In Break',
              key: AGENT_STATE.break,
              value: {
                percent:
                  (this.businessProcessStats.getValue('businessProcessInBreakAgents') * 100) /
                  (this.businessProcessStats.totalBreakAgents || 1),
                count: this.businessProcessStats.getValue('businessProcessInBreakAgents')
              },
              color: 'var(--v-warning-darken2)'
            },
            {
              label: 'No Answer',
              key: AGENT_STATE.noAnswer,
              value: {
                percent:
                  (this.businessProcessStats.getValue('businessProcessNoAnswerAgents') * 100) /
                  (this.businessProcessStats.totalBreakAgents || 1),
                count: this.businessProcessStats.getValue('businessProcessNoAnswerAgents')
              },
              color: 'var(--v-warning-darken3)'
            }
          ]
        }
      ];
    }
  },

  methods: {
    filterBpAgentsByStateId(agentStateId) {
      this.$emit('filter-agents', { agentStateId });
    }
  }
};
</script>

<style src="./scoped.scss" scoped lang="scss" />
